(function () {
  'use strict';

  var root;
  var panel;
  var refreshPromise;
  var giftSyncing = false;
  var lastFocused;
  var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
  var productCache = {};
  var legacyGiftCleanupKey = 'ky-gift-cleanup-v1';

  function isDE() {
    return /^de/i.test(document.documentElement.lang || '');
  }

  function L(fr, de) {
    return isDE() ? de : fr;
  }

  function money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
      style: 'currency',
      currency: currency
    }).format((Number(cents) || 0) / 100);
  }

  function escapeHtml(value) {
    var node = document.createElement('div');
    node.textContent = value == null ? '' : String(value);
    return node.innerHTML;
  }

  function imageUrl(url, width) {
    if (!url) return '';
    try {
      var parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', width);
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  function request(url, options) {
    // Prefix the locale root so /de pages get localized cart payloads; root is '/' on the default locale.
    var localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (url.charAt(0) === '/' && url.indexOf(localeRoot) !== 0) url = localeRoot.replace(/\/$/, '') + url;
    var config = options || {};
    config.headers = Object.assign({
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }, config.headers || {});
    if (typeof fetch === 'function') return fetch(url, config).then(function (response) {
      if (!response.ok) {
        return response.json().catch(function () { return {}; }).then(function (body) {
          throw new Error(body.description || body.message || L('Erreur panier', 'Warenkorbfehler'));
        });
      }
      return response.json();
    });

    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open(config.method || 'GET', url);
      Object.keys(config.headers).forEach(function (key) {
        xhr.setRequestHeader(key, config.headers[key]);
      });
      xhr.onload = function () {
        var body = {};
        try {
          body = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        } catch (error) {
          body = {};
        }
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(new Error(body.description || body.message || L('Erreur panier', 'Warenkorbfehler')));
          return;
        }
        resolve(body);
      };
      xhr.onerror = function () {
        reject(new Error(L('Erreur panier', 'Warenkorbfehler')));
      };
      xhr.send(config.body || null);
    });
  }

  function productHandle(item) {
    var url = item.url || '';
    var match = url.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function productData(item) {
    var handle = productHandle(item);
    if (!handle) return Promise.resolve(null);
    if (!productCache[handle]) {
      productCache[handle] = request('/products/' + handle + '.js').catch(function () { return null; });
    }
    return productCache[handle];
  }

  function variantCompareAt(item, product) {
    if (!product || !product.variants) return 0;
    var variant = product.variants.find(function (entry) {
      return Number(entry.id) === Number(item.variant_id);
    });
    return variant ? Number(variant.compare_at_price || 0) : 0;
  }

  function isGift(item) {
    return Boolean(item.properties && (item.properties._gift === true || item.properties._gift === 'true'));
  }

  /* Unified gift-line identity (2026-09-14, Cadeau Mystère 80 € threshold gift).
     A line counts as THE gift either because it carries the _gift property (added by
     syncGift's own /cart/add.js call) OR because its product is the configured gift
     product (added by hand, or via the Douceur card's plain add-to-cart button, which
     does not set _gift). Both paths must resolve to the same single gift line - never
     a second one - so every gift-identity check in this file goes through this helper. */
  function isGiftLine(item) {
    if (isGift(item)) return true;
    var giftProductId = root && root.dataset.giftProductId;
    return Boolean(giftProductId) && Number(item.product_id) === Number(giftProductId);
  }

  /* Cadeau mystère · 80 € threshold gift (BXGY discount, 2026-09-14) and the older
     Douceur de la Mer necklace gift (BXGY 1928105427291, 2026-08-27) share the same
     product. Whichever BXGY rule currently qualifies makes the line free; when neither
     does, Shopify prices the line at full price on the next cart read. The gift must
     never be charged, so this guard removes the line the moment that happens - not
     because the necklace left the cart (the 80 € rule can keep it free on its own),
     and not because the cart dropped under 80 € (the necklace can keep it free too). */
  var DOUCEUR_PRODUCT_ID = 10663185711451;
  var DOUCEUR_GIFT_PRODUCT_ID = 10672512991579;
  var douceurGiftRemoving = false;
  var giftBlockedTotal = null;
  /* Anti-ping-pong key = qualifying total + applied discount codes (2026-10-10): when the code set changes
     (e.g. the wallet swaps a stale TAROT20 for the credit), the gift is tried again even if the total did not move. */
  function giftBlockKey(cart) {
    return qualifyingTotal(cart) + '|' + ((cart && cart.discount_codes) || []).map(function (d) {
      return String(d.code || '') + ':' + (d.applicable ? 1 : 0);
    }).join(',');
  }
  function renderDouceurGift(cart) {
    var card = root.querySelector('[data-ky-douceur-gift]');
    if (!card) return;
    var hasDouceur = cart.items.some(function (item) {
      return Number(item.product_id) === DOUCEUR_PRODUCT_ID;
    });
    var giftLine = cart.items.find(isGiftLine);
    /* Charged guard: remove the gift line when and only when Shopify did not apply a
       free-gift discount to it (final_line_price > 0) - below threshold, a competing
       non-combinable offer, or any other reason. */
    var giftCharged = Boolean(giftLine) && Number(giftLine.final_line_price || 0) > 0;
    if (giftLine && giftCharged && !douceurGiftRemoving) {
      douceurGiftRemoving = true;
      giftBlockedTotal = giftBlockKey(cart);
      request('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: giftLine.key, quantity: 0 })
      }).then(function (cleanCart) {
        douceurGiftRemoving = false;
        render(cleanCart);
      }).catch(function () {
        douceurGiftRemoving = false;
      });
    }
    var giftFree = Boolean(giftLine) && !giftCharged;
    card.hidden = !hasDouceur || giftCharged;
    card.classList.toggle('is-done', giftFree);
    var cta = card.querySelector('[data-ky-douceur-gift-cta]');
    var done = card.querySelector('[data-ky-douceur-gift-done]');
    var btn = card.querySelector('.kc-go-add');
    if (cta) cta.hidden = giftFree;
    if (done) done.hidden = !giftFree;
    if (btn) btn.hidden = giftFree;
  }

  function legacyGiftCleanupDone() {
    try {
      return localStorage.getItem(legacyGiftCleanupKey) === 'done';
    } catch (error) {
      return false;
    }
  }

  function markLegacyGiftCleanupDone() {
    try {
      localStorage.setItem(legacyGiftCleanupKey, 'done');
    } catch (error) {
      /* The cleanup can safely run again when storage is unavailable. */
    }
  }

  function cleanupLegacyGift() {
    if (!root || root.dataset.thresholdMode === 'dual' || legacyGiftCleanupDone()) return Promise.resolve();

    return request('/cart.js').then(function (cart) {
      var giftLine = cart.items.find(isGift);
      if (!giftLine) {
        markLegacyGiftCleanupDone();
        return cart;
      }

      return request('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: giftLine.key, quantity: 0 })
      }).then(function (cleanCart) {
        markLegacyGiftCleanupDone();
        if (/^\/cart(?:\/|$)/.test(window.location.pathname)) {
          window.location.reload();
          return cleanCart;
        }
        return render(cleanCart);
      });
    }).catch(function () {
      /* Leave the marker unset so a transient cart error is retried next load. */
    });
  }

  function bogoIdList() {
    return String(root.dataset.bogoIds || '').split(',').filter(Boolean).map(Number);
  }

  function bogoQuantity(cart) {
    var ids = bogoIdList();
    if (!ids.length) return 0;
    return cart.items.reduce(function (n, item) {
      return n + (ids.indexOf(Number(item.product_id)) !== -1 ? Number(item.quantity || 0) : 0);
    }, 0);
  }

  function bogoSentence(qty) {
    if (qty === 1) return L('Ajoutez un 2ᵉ mala, il vous est offert.', 'Lege eine 2. Mala dazu, sie ist geschenkt.');
    var free = Math.floor(qty / 2);
    if (free === 1) return L('2ᵉ mala offert ✓', '2. Mala geschenkt ✓');
    if (free > 1) return L(free + ' malas offerts ✓', free + ' Malas geschenkt ✓');
    return '';
  }

  /* Pre-credit item total: what checkout uses for the 80 € Cadeau Mystère (verified 2026-10-08, an 85 € cart
     with a 10,28 € credit still gets the gift free). Feeds the gift message, gift auto-add and the gift guard. */
  function qualifyingTotal(cart) {
    return cart.items.reduce(function (total, item) {
      return total + ((isGiftLine(item) || isHwGiftLine(item)) ? 0 : Number(item.final_line_price || 0));
    }, 0);
  }

  /* HALLOWEEN 2026 · mini bracelet de protection offert dès 60 € (automatic BXGY 1942261858651).
     From 80 € "HALLOWEEN 80" (automatic BXGY 1942490775899) gives Cadeau Mystère + the same mini together (Reda 2026-10-15;
     CM 80 1929827352923 ends at the window start, clone 1942490743131 resumes after); nothing here changes for it: the drawer still auto-adds the Cadeau Mystère at 80 €
     and the customer's _hw_gift mini stays the single mini line.
     Customer picks 1 of 3 minis in the drawer once the basket (gift excluded) reaches 60 €.
     The picker only exists inside the discount window (Paris Fri Oct 23 08:00 -> Sun Nov 1 23:59:59).
     QA before the window: add ?hw_preview=1 to any URL (stored in localStorage, ?hw_preview=0 clears it).
     The gift line carries the hidden property _hw_gift, NOT _gift: _gift is the Cadeau Mystère identity
     (isGift/isGiftLine) and reusing it would make every Cadeau Mystère routine treat the mini as that gift.
     Truth comes from Shopify: the charged guard below removes the mini whenever the discount did not make
     it free (basket under 60 €, offer over, or a competing gift won the cart). See BUILD-SHOP.md. */
  /* ONE place to move the campaign window (UTC). Must match the discounts' startsAt/endsAt and the
     starts_at/ends_at settings of the cbh-picker section in templates/page.halloween-bracelet-offert.json. */
  var HW_WINDOW = { start: '2026-10-23T06:00:00Z', end: '2026-11-01T22:59:59Z' };
  var HW_GIFT = {
    start: Date.parse(HW_WINDOW.start),
    end: Date.parse(HW_WINDOW.end),
    at: 6000,
    prop: '_hw_gift',
    choices: [
      { id: 47660440093019, handle: 'mini-bracelet-en-obsidian', stone: 'Obsidienne' },
      { id: 39578939424877, handle: 'mini-energie-oeil-tigre', stone: 'Œil de Tigre' },
      { id: 46988910821723, handle: 'mini-bracelet-energie-en-hematite', stone: 'Hématite' }
    ]
  };
  var hwRemoving = false;
  var hwBusy = false;
  var hwBlockedKey = null;
  var hwAutoKey = null;

  function hwSavedChoice() {
    try {
      var saved = Number(localStorage.getItem('ky-hw-choice') || 0);
      return hwChoice(saved) ? saved : 0;
    } catch (error) {
      return 0;
    }
  }

  function hwPreview() {
    try {
      var flag = new URLSearchParams(window.location.search).get('hw_preview');
      if (flag === '1') localStorage.setItem('ky-hw-preview', '1');
      if (flag === '0') localStorage.removeItem('ky-hw-preview');
      return localStorage.getItem('ky-hw-preview') === '1';
    } catch (error) {
      return false;
    }
  }

  function hwActive() {
    var now = Date.now();
    return (now >= HW_GIFT.start && now <= HW_GIFT.end) || hwPreview();
  }

  function isHwGiftLine(item) {
    var p = item && item.properties;
    return Boolean(p && p[HW_GIFT.prop] && String(p[HW_GIFT.prop]) !== 'false');
  }

  function hwChoice(variantId) {
    return HW_GIFT.choices.find(function (c) { return Number(c.id) === Number(variantId); }) || null;
  }

  /* Basket that can unlock the mini. Shopify's BXGY engine (tested 2026-10-14 on draft-order calculation)
     does not count gift lines, and does not count malas already paired by the 1+1 offer, toward another
     gift's threshold. The drawer mirrors that so it never invites a pick that checkout would charge. */
  function hwQualifyingTotal(cart) {
    var bogoIds = bogoIdList();
    var bogoQty = bogoQuantity(cart);
    return cart.items.reduce(function (total, item) {
      if (isGiftLine(item) || isHwGiftLine(item)) return total;
      if (bogoQty >= 2 && bogoIds.indexOf(Number(item.product_id)) !== -1) return total;
      return total + Number(item.final_line_price || 0);
    }, 0);
  }

  function hwKey(cart) {
    return hwQualifyingTotal(cart) + '|' + cart.items.filter(function (item) { return !isHwGiftLine(item); }).map(function (item) {
      return item.variant_id + 'x' + item.quantity;
    }).join(',') + '|' + ((cart && cart.discount_codes) || []).map(function (d) { return d.code; }).join(',');
  }

  function hwStyles() {
    if (document.getElementById('ky-hw-gift-css')) return;
    var style = document.createElement('style');
    style.id = 'ky-hw-gift-css';
    style.textContent = ''
      + '.kycart .kc-hw{margin:14px 18px 0;padding:14px;border:1px dashed #9c5291;border-radius:12px;background:#f7f1f8;font-family:var(--font-body,sans-serif)}'
      + '.kycart .kc-hw[hidden]{display:none}'
      + '.kycart .kc-hw.is-done{border-style:solid;border-color:#cdb2d8;background:#faf6fb}'
      + '.kycart .kc-hw-k{display:block;margin:0 0 2px;font-family:var(--font-mono,monospace);font-size:10.5px;letter-spacing:.14em;text-transform:uppercase;color:#9c5291}'
      + '.kycart .kc-hw-t{display:block;margin:0 0 10px;font-family:var(--font-heading,serif);font-size:14.5px;line-height:1.3;color:#2a2730}'
      + '.kycart .kc-hw-list{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}'
      + '.kycart .kc-hw-opt{display:flex;flex-direction:column;align-items:center;gap:6px;margin:0;padding:8px 6px 9px;border:1px solid #e7e3e9;border-radius:10px;background:#fff;cursor:pointer;font:inherit;color:#2a2730;text-align:center}'
      + '.kycart .kc-hw-opt img{display:block;width:100%;max-width:72px;aspect-ratio:1;object-fit:cover;border-radius:8px;background:#f4eded}'
      + '.kycart .kc-hw-opt span{font-family:var(--font-body,sans-serif);font-size:12px;line-height:1.25;color:#2a2730}'
      + '.kycart .kc-hw-opt b{font-family:var(--font-mono,monospace);font-size:10px;font-weight:400;letter-spacing:.08em;text-transform:uppercase;color:#9c5291}'
      + '.kycart .kc-hw-opt.is-picked{border-color:#9c5291;box-shadow:0 0 0 1px #9c5291 inset}'
      + '.kycart .kc-hw-opt[disabled]{cursor:default}'
      + '.kycart .kc-hw-n{display:block;margin:9px 0 0;font-size:12px;line-height:1.35;color:#7a7880}';
    document.head.appendChild(style);
  }

  function hwCard() {
    var card = root.querySelector('[data-ky-hw-gift]');
    if (card) return card;
    var lines = root.querySelector('[data-ky-cart-lines]');
    if (!lines || !lines.parentNode) return null;
    hwStyles();
    card = document.createElement('div');
    card.className = 'kc-hw';
    card.setAttribute('data-ky-hw-gift', '');
    card.hidden = true;
    card.innerHTML = '<span class="kc-hw-k">' + escapeHtml(L('Halloween · offert dès 60 €', 'Halloween · ab 60 € geschenkt')) + '</span>'
      + '<strong class="kc-hw-t" data-ky-hw-title></strong>'
      + '<div class="kc-hw-list">' + HW_GIFT.choices.map(function (c) {
        return '<button type="button" class="kc-hw-opt" data-ky-hw-choose="' + c.id + '"><img alt="" width="72" height="72" loading="lazy" data-ky-hw-img="' + escapeHtml(c.handle) + '"><span>' + escapeHtml(c.stone) + '</span><b data-ky-hw-state></b></button>';
      }).join('') + '</div>'
      + '<span class="kc-hw-n">' + escapeHtml(L('Mini bracelet en pierre naturelle, un par commande. Montant calculé hors mini bracelet offert.', 'Mini-Armband aus Naturstein, eines pro Bestellung.')) + '</span>';
    lines.parentNode.insertBefore(card, lines);
    HW_GIFT.choices.forEach(function (c) {
      productData({ url: '/products/' + c.handle }).then(function (product) {
        var img = card.querySelector('[data-ky-hw-img="' + c.handle + '"]');
        if (img && product && product.featured_image) img.src = imageUrl(product.featured_image, 160);
      });
    });
    return card;
  }

  function renderHwGift(cart) {
    var hwLine = cart.items.find(isHwGiftLine);
    /* Charged guard (runs in and out of the window): a mini carrying _hw_gift that Shopify did not make free
       is removed at once, so nobody pays for the gift by accident and leftovers vanish after Nov 1. */
    if (hwLine && Number(hwLine.final_line_price || 0) > 0 && !hwRemoving) {
      hwRemoving = true;
      hwBlockedKey = hwKey(cart);
      request('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: hwLine.key, quantity: 0 })
      }).then(function (cleanCart) {
        hwRemoving = false;
        render(cleanCart);
      }).catch(function () {
        hwRemoving = false;
      });
      hwLine = null;
    }
    if (hwBlockedKey !== null && hwKey(cart) !== hwBlockedKey) hwBlockedKey = null;

    var card = hwCard();
    if (!card) return;
    var eligible = hwActive() && Number(cart.item_count || 0) > 0
      && hwQualifyingTotal(cart) >= HW_GIFT.at && hwBlockedKey === null;
    var free = Boolean(hwLine) && Number(hwLine.final_line_price || 0) === 0;
    /* A pick made on /pages/halloween-bracelet-offert under 60 € is remembered and added once the basket qualifies. */
    var saved = hwSavedChoice();
    if (eligible && !hwLine && saved && !hwBusy && !hwRemoving && hwAutoKey !== hwKey(cart)) {
      hwAutoKey = hwKey(cart);
      hwChoose(saved);
    }
    card.hidden = !(hwActive() && (free || eligible));
    card.classList.toggle('is-done', free);
    var title = card.querySelector('[data-ky-hw-title]');
    if (title) {
      title.textContent = free
        ? L('✓ Votre mini bracelet de protection est offert', '✓ Dein Mini-Schutzarmband ist geschenkt')
        : L('Choisissez votre mini bracelet de protection offert', 'Wähle dein geschenktes Mini-Schutzarmband');
    }
    card.querySelectorAll('[data-ky-hw-choose]').forEach(function (btn) {
      var picked = free && Number(btn.dataset.kyHwChoose) === Number(hwLine.variant_id);
      btn.classList.toggle('is-picked', picked);
      btn.disabled = picked || hwBusy;
      var state = btn.querySelector('[data-ky-hw-state]');
      if (state) state.textContent = picked ? L('Offert ✓', 'Geschenkt ✓') : (free ? L('Changer', 'Tauschen') : L('Choisir', 'Wählen'));
    });
  }

  function hwChoose(variantId) {
    if (hwBusy || !hwChoice(variantId)) return Promise.resolve();
    hwBusy = true;
    try { localStorage.setItem('ky-hw-choice', String(variantId)); } catch (error) { /* optional */ }
    return request('/cart.js').then(function (cart) {
      var current = cart.items.find(isHwGiftLine);
      var drop = current
        ? request('/cart/change.js', { method: 'POST', body: JSON.stringify({ id: current.key, quantity: 0 }) })
        : Promise.resolve();
      return drop.then(function () {
        var props = {};
        props[HW_GIFT.prop] = '1';
        return request('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: props }] })
        });
      });
    }).then(function () {
      hwBusy = false;
      return refresh();
    }).catch(function (error) {
      hwBusy = false;
      console.error(error);
      return refresh();
    });
  }

  /* Post-credit total: what checkout uses for the 60 € free-shipping rate (verified 2026-10-04/08, a 60 € cart
     with a 10,28 € credit pays 8,95 €). Item total minus cart.cart_level_discount_applications. Shipping leg only. */
  function cartLevelDiscount(cart) {
    return ((cart && cart.cart_level_discount_applications) || []).reduce(function (sum, app) {
      return sum + Number(app.total_allocated_amount || 0);
    }, 0);
  }

  function shippingTotal(cart) {
    return Math.max(0, qualifyingTotal(cart) - cartLevelDiscount(cart));
  }

  /* The Cadeau Mystère line never counts toward "Vous économisez" (2026-10-08): its cart price is a stand-in,
     the gift's value is only ever stated as 30 €, so 14,90 € must not surface here. */
  function cartSavings(cart) {
    return cart.items.reduce(function (total, item) {
      if (isGiftLine(item)) return total;
      var original = Number(item.original_line_price || 0);
      if (!original && Number(item.original_price || 0) > Number(item.final_price || 0)) {
        original = Number(item.original_price || 0) * Number(item.quantity || 1);
      }
      var finalPrice = Number(item.final_line_price || 0);
      return total + Math.max(0, original - finalPrice);
    }, 0);
  }

  function compareAtSavings(cart) {
    return Promise.all(cart.items.map(function (item) {
      if (isGiftLine(item)) return Promise.resolve(0);
      return productData(item).then(function (product) {
        var compareAt = variantCompareAt(item, product);
        var finalUnit = Number(item.final_price || 0);
        if (compareAt <= finalUnit) return 0;
        return (compareAt - finalUnit) * Number(item.quantity || 1);
      });
    })).then(function (lines) {
      return lines.reduce(function (total, value) { return total + value; }, 0);
    });
  }

  function bestLineComparePrice(item, product) {
    if (isGift(item)) return 0;
    var finalLine = Number(item.final_line_price || 0);
    var originalLine = Number(item.original_line_price || 0);
    var compareAt = variantCompareAt(item, product);
    var variantCompareLine = compareAt > 0 ? compareAt * Number(item.quantity || 1) : 0;
    return Math.max(originalLine, variantCompareLine) > finalLine
      ? Math.max(originalLine, variantCompareLine)
      : 0;
  }

  function priceMarkup(item, compareLinePrice) {
    var hasCompare = Number(compareLinePrice || 0) > Number(item.final_line_price || 0);
    if (isGift(item) || (isHwGiftLine(item) && Number(item.final_line_price || 0) === 0)) {
      return '<div class="kc-price price price--on-sale" data-ky-line-price><span class="price-item price-item--sale">Offert</span>'
        + (hasCompare ? '<s class="price-item price-item--regular">' + money(compareLinePrice) + '</s>' : '')
        + '</div>';
    }
    return '<div class="kc-price price' + (hasCompare ? ' price--on-sale' : '') + '" data-ky-line-price>'
      + '<span class="price-item ' + (hasCompare ? 'price-item--sale' : 'price-item--regular') + '">' + money(item.final_line_price) + '</span>'
      + (hasCompare ? '<s class="price-item price-item--regular">' + money(compareLinePrice) + '</s>' : '')
      + '</div>';
  }

  function updateLineComparePrices(cart) {
    if (!root || !cart || !cart.items) return;
    cart.items.forEach(function (item, index) {
      if (isGift(item)) return;
      productData(item).then(function (product) {
        var compareLinePrice = bestLineComparePrice(item, product);
        if (!compareLinePrice) return;
        var line = root.querySelector('[data-line-index="' + index + '"]');
        if (!line || line.dataset.lineKey !== item.key) return;
        var node = line.querySelector('[data-ky-line-price]');
        if (node) node.outerHTML = priceMarkup(item, compareLinePrice);
      });
    });
  }

  function updateCounts(cart) {
    document.querySelectorAll('[data-ky-cart-count], [data-cart-count], [data-ky-cart-bubble]').forEach(function (node) {
      node.textContent = cart.item_count;
      node.hidden = cart.item_count === 0;
    });
  }

  function lineSubline(item) {
    if (item.variant_title && item.variant_title !== 'Default Title') return item.variant_title;
    if (item.options_with_values && item.options_with_values.length) {
      return item.options_with_values
        .map(function (option) { return option.value; })
        .filter(function (value) { return value && value !== 'Default Title'; })
        .join(' · ');
    }
    return '';
  }

  function updateSavings(cart, baseSavings) {
    var savingsRow = root.querySelector('[data-ky-cart-savings]');
    if (!savingsRow) return;
    var value = root.querySelector('[data-ky-cart-savings-value]');
    var apply = function (savings) {
      savingsRow.hidden = root.dataset.showSavings !== 'true' || savings <= 0;
      if (value) value.textContent = '− ' + money(savings);
    };

    apply(baseSavings);
    compareAtSavings(cart).then(function (compareSavings) {
      apply(Math.max(baseSavings, compareSavings));
    });
  }

  /* Wallet credit row (cb-wallet, 2026-10-04): show the cart-level CREDIT-* code above the total.
     Note: cart.js can list a segment-gated code as applied for a visitor that checkout later refuses. */
  var CREDIT_LABELS = {
    'CREDIT-COMPTE': ["Crédit d'achat", 'Kontoguthaben'],
    'CREDIT-BIENVENUE': ["Crédit d'achat", 'Willkommensguthaben'],
    'CREDIT-V2-A': ["Crédit d'achat", 'Kontoguthaben'],
    'CREDIT-V2-B': ["Crédit d'achat", 'Kontoguthaben']
  };

  function creditKey(app) {
    var title = String((app && app.title) || '').toUpperCase();
    if (CREDIT_LABELS[title]) return title;
    var key = String((app && app.key) || '').toUpperCase();
    return CREDIT_LABELS[key] ? key : '';
  }

  function renderCredit(cart) {
    var box = root.querySelector('[data-ky-cart-credit]');
    if (!box) return;
    var rows = (cart.cart_level_discount_applications || []).filter(function (app) {
      return creditKey(app) && Number(app.total_allocated_amount || 0) > 0;
    });
    box.innerHTML = rows.map(function (app) {
      var lab = CREDIT_LABELS[creditKey(app)];
      var ck = creditKey(app);
      return '<div class="kc-credit-row"><span>' + escapeHtml(L(lab[0], lab[1])) + '</span><strong>−' + money(app.total_allocated_amount) + '</strong></div>'
        + (ck !== 'CREDIT-BIENVENUE' ? '<p class="kc-credit-count" data-cbw-count data-cbw-code="' + ck + '" hidden></p>' : '');
    }).join('') + (rows.length ? '<p class="kc-credit-note">' + escapeHtml(L('Utilisable uniquement sur vos achats, sans valeur monétaire.', 'Nur für Einkäufe einlösbar, ohne Geldwert.')) + '</p>'
      + '<p class="kc-credit-note kc-credit-email">' + escapeHtml(L('Crédit lié à votre email : utilisez la même adresse au paiement.', 'Guthaben an Ihre E-Mail gebunden: bitte beim Bezahlen dieselbe Adresse verwenden.')) + '</p>' : '');
    box.hidden = rows.length === 0;
    if (window.cbwCountRefresh) window.cbwCountRefresh();
  }

  function updateShipLine(cart) {
    if (!isDE()) return;
    var shipLine = root.querySelector('[data-ky-cart-shipline]');
    if (!shipLine) return;
    shipLine.textContent = Number(cart.total_price || 0) >= 6000
      ? 'Kostenloser Versand ✓'
      : 'Versand: 8,95 € · kostenlos ab 60 €';
  }

  function lineMarkup(item, index) {
    if (isHwGiftLine(item)) return hwLineMarkup(item, index);
    var gift = isGiftLine(item);
    var image = item.image
      ? '<img src="' + escapeHtml(imageUrl(item.image, 160)) + '" alt="' + escapeHtml(item.product_title) + '" width="64" height="64">'
      : '';
    var compare = bestLineComparePrice(item, null);
    var controls = gift
      ? '<span class="kc-gift-tag">' + L('Offert · dès ', 'Geschenkt · ab ') + escapeHtml(Number(root.dataset.giftAt || 0) / 100) + '€</span>'
      : '<div class="kc-qty"><button type="button" data-ky-line-delta="-1" aria-label="' + L('Réduire la quantité', 'Menge verringern') + '">−</button><span>' + item.quantity + '</span><button type="button" data-ky-line-delta="1" aria-label="' + L('Augmenter la quantité', 'Menge erhöhen') + '">+</button></div>';
    var price = priceMarkup(item, compare);
    var remove = gift ? '' : '<button class="kc-rm" type="button" data-ky-line-remove aria-label="' + L('Retirer', 'Entfernen') + '">✕</button>';

    return '<article class="kc-item' + (gift ? ' is-gift' : '') + '" data-line-key="' + escapeHtml(item.key) + '" data-line-index="' + index + '" data-line-quantity="' + item.quantity + '">'
      + '<a class="kc-thumb" href="' + escapeHtml(item.url) + '">' + image + '</a>'
      + '<div class="kc-meta"><a class="kc-nm" href="' + escapeHtml(item.url) + '">' + escapeHtml(item.product_title) + '</a>'
      + (gift ? controls : '<div class="kc-sub">' + escapeHtml(lineSubline(item)) + '</div>')
      + '<div class="kc-row">' + (gift ? '<span></span>' : controls) + price + '</div></div>' + remove + '</article>';
  }

  /* Halloween mini gift line: no quantity stepper (one per order), a remove cross so the customer can drop it. */
  function hwLineMarkup(item, index) {
    var image = item.image
      ? '<img src="' + escapeHtml(imageUrl(item.image, 160)) + '" alt="' + escapeHtml(item.product_title) + '" width="64" height="64">'
      : '';
    var tag = '<span class="kc-gift-tag">' + L('Mini bracelet de protection offert · dès 60 €', 'Mini-Schutzarmband geschenkt · ab 60 €') + '</span>';
    return '<article class="kc-item is-gift" data-ky-hw-line data-line-key="' + escapeHtml(item.key) + '" data-line-index="' + index + '" data-line-quantity="' + item.quantity + '">'
      + '<a class="kc-thumb" href="' + escapeHtml(item.url) + '">' + image + '</a>'
      + '<div class="kc-meta"><a class="kc-nm" href="' + escapeHtml(item.url) + '">' + escapeHtml(item.product_title) + '</a>'
      + tag + '<div class="kc-row"><span></span>' + priceMarkup(item, Number(item.original_line_price || 0)) + '</div></div>'
      + '<button class="kc-rm" type="button" data-ky-line-remove aria-label="' + L('Retirer', 'Entfernen') + '">✕</button></article>';
  }

  function renderThreshold(cart) {
    var threshold = root.querySelector('[data-ky-threshold]');
    if (!threshold || root.dataset.thresholdMode === 'off') return;

    var total = shippingTotal(cart);
    var giftTotal = qualifyingTotal(cart);
    var freeAt = Number(root.dataset.freeShipAt || 6000);
    var percent = freeAt > 0 ? Math.min(100, total / freeAt * 100) : 100;

    var bogoIds = String(root.dataset.bogoIds || '').split(',').filter(Boolean).map(Number);
    var bogoQty = cart.items.reduce(function (n, item) {
      return n + (bogoIds.indexOf(Number(item.product_id)) !== -1 ? Number(item.quantity || 0) : 0);
    }, 0);

    /* Cadeau Mystère 80 € threshold gift (2026-09-14): dual mode with a gift variant
       configured adds a second leg to the shipping message once shipping is unlocked. */
    var giftAt = Number(root.dataset.giftAt || 0);
    var giftVariantId = Number(root.dataset.giftVariantId || 0);
    var dualGift = root.dataset.thresholdMode === 'dual' && giftVariantId > 0;

    var message;
    if (giftTotal === 0) {
      message = L('Votre panier est vide - ajoutez une pierre pour commencer.', 'Dein Warenkorb ist leer - fuege einen Stein hinzu, um loszulegen.');
    } else if (total < freeAt) {
      message = L('Plus que <b>', 'Nur noch <b>') + money(freeAt - total) + L('</b> pour la <b>livraison offerte</b>', '</b> bis zur <b>kostenlosen Lieferung</b>');
      if (hwActive() && !isDE()) message += ' et votre <b>mini bracelet de protection offert</b>';
    } else if (dualGift && giftTotal < giftAt) {
      message = L('Livraison offerte ✓ · Plus que <b>', 'Kostenloser Versand ✓ · Nur noch <b>') + money(giftAt - giftTotal) + L('</b> pour votre <b>Cadeau Mystère offert</b>', '</b> bis zu deinem <b>Geschenk</b>');
    } else if (dualGift) {
      message = L('Livraison offerte ✓ · Cadeau Mystère offert ✓', 'Kostenloser Versand ✓ · Geschenk ✓');
    } else {
      message = L('Livraison offerte ✓', 'Kostenloser Versand ✓');
    }

    if (bogoQty === 1) {
      message += L(' · ajoutez un <b>2ᵉ mala</b>, il vous est offert', ' · lege eine <b>2. Mala</b> dazu, sie ist geschenkt');
    } else if (bogoQty >= 2) {
      var freeCount = Math.floor(bogoQty / 2);
      message += L(' · ' + (freeCount > 1 ? freeCount + ' malas offerts' : '2ᵉ mala offert') + ' ✓',
                   ' · ' + (freeCount > 1 ? freeCount + ' Malas geschenkt' : '2. Mala geschenkt') + ' ✓');
    }

    threshold.classList.toggle('is-unlocked', total >= freeAt);
    var messageNode = root.querySelector('[data-ky-threshold-message]');
    var barNode = root.querySelector('[data-ky-threshold-bar]');
    if (messageNode) messageNode.innerHTML = message;
    if (barNode) barNode.style.width = percent + '%';

    var shipTick = root.querySelector('[data-ky-ship-tick]');
    var giftTick = root.querySelector('[data-ky-gift-tick]');
    if (shipTick) {
      shipTick.classList.toggle('is-reached', total >= freeAt);
      shipTick.textContent = L('Livraison offerte ✓', 'Kostenloser Versand ✓');
    }
    /* The 59 € gift offer expired 2026-08-04. Its progress line printed a negative number
       on every cart for twelve days. Hidden rather than removed so the markup stays stable. */
    if (giftTick) {
      giftTick.textContent = '';
      giftTick.style.display = 'none';
    }
  }

  function renderTrioProgress(cart) {
    var threshold = root.querySelector('[data-ky-threshold]');
    var messageNode = root.querySelector('[data-ky-threshold-message]');
    var barNode = root.querySelector('[data-ky-threshold-bar]');
    if (!threshold || !messageNode || !barNode) return;

    var barShell = barNode.parentElement;
    var hasItems = Number(cart.item_count || 0) > 0;
    if (!hasItems) {
      threshold.hidden = true;
      threshold.dataset.kyThresholdKind = 'trio';
      threshold.classList.toggle('is-unlocked', false);
      messageNode.innerHTML = '';
      barNode.style.width = '0%';
      if (barShell) {
        barShell.setAttribute('aria-valuenow', '0');
        barShell.setAttribute('aria-valuetext', L('Aucune pièce éligible dans le panier.', 'Kein berechtigtes Stück im Warenkorb.'));
      }
      return;
    }

    /* B2G3 is scoped to the Collection Ete only. Count ONLY products inside that scope,
       otherwise the drawer promises a trio on items the discount will never touch. */
    /* 2026-10-10: the B2G3 discount (DiscountAutomaticNode/1919294734683) EXPIRED 2026-09-07. While it is off the
       trio bar is removed: the drawer keeps renderThreshold()'s 60 € shipping / 80 € gift message, which already
       carries the mala 1+1 line (BOGO 1925805801819 still active). Flip to true on relaunch. */
    var B2G3_ACTIVE = false;
    var b2g3Ids = B2G3_ACTIVE ? String(root.dataset.b2g3Ids || '').split(',').filter(Boolean).map(Number) : [];
    var eligibleItems = cart.items.filter(function (item) {
      return !isGift(item) && b2g3Ids.indexOf(Number(item.product_id)) !== -1;
    });
    var bogoQty = bogoQuantity(cart);
    var eligibleCount = eligibleItems.reduce(function (total, item) {
      return total + Number(item.quantity || 0);
    }, 0);

    /* Nothing trio/BOGO-specific to say for this cart: leave the shipping/Cadeau Mystère
       message renderThreshold() just wrote alone instead of stomping it with the generic
       "Offre 2 + 1" copy (2026-09-14 - this unconditional overwrite was silently hiding
       the free-shipping and 80 € gift threshold messages on every non-trio cart). */
    if (!B2G3_ACTIVE || (eligibleCount === 0 && bogoQty === 0)) {
      threshold.hidden = false;
      threshold.dataset.kyThresholdKind = 'ship';
      return;
    }

    var progress = Math.min(eligibleCount, 3);
    var title;
    var detail;

    if (eligibleCount === 0 && bogoQty > 0) {
      title = L('1 acheté = 1 offert', '1 gekauft = 1 geschenkt');
      detail = bogoSentence(bogoQty);
    } else if (eligibleCount === 0) {
      title = L('Offre 2 + 1', 'Angebot 2 + 1');
      detail = L('3 pièces de la Collection Été : la moins chère est offerte.', 'Drei Artikel der Sommer-Kollektion: der günstigste wird geschenkt.');
    } else if (eligibleCount === 1) {
      title = L('Votre trio · 1 / 3', 'Dein Trio · 1 / 3');
      detail = L('Encore 2 pièces, et la moins chère est offerte.', 'Nur noch 2 Artikel.');
    } else if (eligibleCount === 2) {
      title = L('Votre trio · 2 / 3', 'Dein Trio · 2 / 3');
      detail = L('Encore 1 pièce, et la moins chère est offerte.', 'Nur noch ein Artikel, und der günstigste wird dir geschenkt.');
    } else {
      var cheapestPrice = eligibleItems.reduce(function (cheapest, item) {
        var price = Number(item.original_price || item.final_price || item.price || 0);
        return price > 0 ? Math.min(cheapest, price) : cheapest;
      }, Infinity);
      title = L('Votre trio · 3 / 3', 'Dein Trio · 3 / 3');
      detail = L('Trio complet : la pièce la moins chère', 'Trio komplett: das günstigste Stück')
        + (Number.isFinite(cheapestPrice) ? ' (' + money(cheapestPrice) + ')' : '')
        + L(' est offerte. 1 trio maximum par commande.', ' wird dir geschenkt. Maximal ein Trio pro Bestellung.');
    }

    if (eligibleCount > 0 && bogoQty > 0) {
      detail += ' · ' + bogoSentence(bogoQty);
    }

    var action = '';
    if (eligibleCount === 0 && bogoQty > 0) {
      action = ' <a class="kc-offer-link" href="/collections/1-achete-1-offert">' + L('Voir les malas', 'Die Malas ansehen') + '</a>';
    } else if (eligibleCount < 3) {
      action = ' <a class="kc-offer-link" href="' + L('/collections/collection-ete', '/de/collections/karma-angebot') + '">' + L('Voir la Collection Été', 'Sommer-Kollektion ansehen') + '</a>';
    }

    threshold.hidden = false;
    threshold.dataset.kyThresholdKind = 'trio';
    threshold.classList.toggle('is-unlocked', eligibleCount >= 3);
    messageNode.innerHTML = '<b>' + escapeHtml(title) + '</b> <span>' + escapeHtml(detail) + '</span>' + action;
    barNode.style.width = (eligibleCount === 0 && bogoQty > 0 ? Math.min(100, bogoQty / 2 * 100) : progress / 3 * 100) + '%';

    if (barShell) {
      barShell.setAttribute('aria-valuenow', String(progress));
      barShell.setAttribute('aria-valuetext', title + '. ' + detail);
    }
  }

  function render(cart) {
    if (!root) return cart;
    var hasItems = cart.item_count > 0;
    var lines = root.querySelector('[data-ky-cart-lines]');
    var empty = root.querySelector('[data-ky-cart-empty]');
    var footer = root.querySelector('[data-ky-cart-footer]');
    var savings = cartSavings(cart);

    updateCounts(cart);
    lines.hidden = !hasItems;
    empty.hidden = hasItems;
    footer.hidden = !hasItems;
    lines.innerHTML = cart.items.map(lineMarkup).join('');
    root.querySelector('[data-ky-cart-total]').textContent = money(cart.total_price);

    updateSavings(cart, savings);
    renderCredit(cart);
    updateShipLine(cart);
    updateLineComparePrices(cart);

    root.querySelectorAll('[data-ky-xsell] [data-product-id]').forEach(function (node) {
      node.hidden = cart.items.some(function (item) {
        return String(item.product_id) === node.dataset.productId;
      });
    });

    renderThreshold(cart);
    renderTrioProgress(cart);
    renderDouceurGift(cart);
    renderHwGift(cart);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('ky:cart:updated', { detail: { cart: cart } }));
    return cart;
  }

  function syncGift(cart) {
    var mode = root.dataset.thresholdMode;
    var giftLine = cart.items.find(isGiftLine);

    if (mode !== 'dual') {
      if (!giftLine || giftSyncing) return Promise.resolve(cart);

      giftSyncing = true;
      return request('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: giftLine.key, quantity: 0 })
      }).finally(function () { giftSyncing = false; });
    }

    var giftVariantId = Number(root.dataset.giftVariantId || 0);
    if (!giftVariantId || giftSyncing) return Promise.resolve(cart);

    var giftAt = Number(root.dataset.giftAt || 5900);
    var total = qualifyingTotal(cart);

    /* Anti-ping-pong: the charged guard (renderDouceurGift) remembers the qualifying
       total at the moment it had to remove a charged gift line. Skip auto-adding again
       while the total sits at that exact value - Shopify would only charge it again
       and the guard would strip it right back out. Any real change in the cart clears
       the block and lets the gift be tried again. */
    if (giftBlockedTotal !== null) {
      if (giftBlockKey(cart) === giftBlockedTotal) return Promise.resolve(cart);
      giftBlockedTotal = null;
    }

    if (giftLine && giftLine.quantity > 1) {
      giftSyncing = true;
      return request('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: giftLine.key, quantity: 1 })
      }).finally(function () { giftSyncing = false; });
    }

    /* Only ADD here. Removing a charged gift is owned exclusively by the charged guard
       in renderDouceurGift, so a legitimately free gift (e.g. via the Douceur necklace
       offer) is never stripped just because qualifyingTotal sits under giftAt. */
    var shouldHaveGift = total >= giftAt;
    if (!shouldHaveGift || giftLine) return Promise.resolve(cart);

    giftSyncing = true;
    return request('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ items: [{ id: giftVariantId, quantity: 1, properties: { _gift: 'true' } }] })
    }).then(function () { return request('/cart.js'); })
      .finally(function () { giftSyncing = false; });
  }

  function refresh() {
    if (!root) return Promise.reject(new Error(L('Tiroir panier indisponible', 'Warenkorb nicht verfügbar')));
    if (refreshPromise) return refreshPromise;
    refreshPromise = request('/cart.js')
      .then(syncGift)
      .then(render)
      .finally(function () { refreshPromise = null; });
    return refreshPromise;
  }

  function pulse() {
    var icon = document.querySelector('[data-kn-cart-open], [data-ky-cart-open], .kn-ico[aria-label="Panier"]');
    if (!icon) return;
    icon.classList.remove('kc-pulse');
    void icon.offsetWidth;
    icon.classList.add('kc-pulse');
  }

  function emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {} }));
  }

  function open() {
    if (!root) return;
    var navDrawer = document.querySelector('.kn-drawer.open');
    if (navDrawer) {
      navDrawer.classList.add('kn-no-anim');
    }
    emit('ky:nav:close');
    window.setTimeout(function () {
      if (navDrawer) navDrawer.classList.remove('kn-no-anim');
    }, 60);
    lastFocused = document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    window.setTimeout(function () { panel.focus(); }, 30);
  }

  function close() {
    if (!root) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  function giftLineForProjectedCart(cartBefore, mainVariantId, addedTotal, properties) {
    if (!root || root.dataset.thresholdMode !== 'dual') return null;
    if (properties && (properties._gift === true || properties._gift === 'true')) return null;

    var giftVariantId = Number(root.dataset.giftVariantId || 0);
    if (!giftVariantId || Number(mainVariantId) === giftVariantId) return null;

    var giftAt = Number(root.dataset.giftAt || 5900);
    var currentTotal = cartBefore ? qualifyingTotal(cartBefore) : 0;
    var hasGift = cartBefore && cartBefore.items && cartBefore.items.some(isGift);
    if (hasGift || currentTotal + Number(addedTotal || 0) < giftAt) return null;

    return { id: giftVariantId, quantity: 1, properties: { _gift: 'true' } };
  }

  function add(variantId, quantity, properties, lineTotal) {
    var id = typeof variantId === 'object' ? variantId.id : variantId;
    var qty = typeof variantId === 'object' ? variantId.quantity : quantity;
    var props = typeof variantId === 'object' ? variantId.properties : properties;
    var addedTotal = typeof variantId === 'object'
      ? Number(variantId.lineTotal || variantId.totalPrice || 0)
      : Number(lineTotal || 0);
    var mainLine = { id: Number(id), quantity: Number(qty) || 1, properties: props || {} };

    return request('/cart.js').catch(function () { return null; }).then(function (cartBefore) {
      var items = [mainLine];
      var giftLine = giftLineForProjectedCart(cartBefore, id, addedTotal, mainLine.properties);
      if (giftLine) items.push(giftLine);
      return request('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: items })
      });
    }).then(function () {
      pulse();
      return refresh();
    }).then(function (cart) {
      emit('ky:cart:open');
      return cart;
    });
  }

  function changeLine(lineOrKey, quantity) {
    var payload = typeof lineOrKey === 'number'
      ? { line: lineOrKey, quantity: Math.max(0, Number(quantity) || 0) }
      : { id: lineOrKey, quantity: Math.max(0, Number(quantity) || 0) };
    return request('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify(payload)
    }).then(syncGift).then(render);
  }

  function onClick(event) {
    if (event.target.closest('[data-ky-cart-close]')) {
      close();
      return;
    }

    var opener = event.target.closest('[data-ky-cart-open]');
    if (opener) {
      event.preventDefault();
      open();
      refresh();
      return;
    }

    var hwButton = event.target.closest('[data-ky-hw-choose]');
    if (hwButton) {
      event.preventDefault();
      hwChoose(hwButton.dataset.kyHwChoose);
      return;
    }

    var addButton = event.target.closest('[data-ky-cart-add]');
    if (addButton) {
      addButton.disabled = true;
      add(addButton.dataset.kyCartAdd, 1).catch(function (error) {
        console.error(error);
      }).finally(function () {
        addButton.disabled = false;
      });
      return;
    }

    var line = event.target.closest('[data-line-key]');
    if (!line) return;
    var delta = event.target.closest('[data-ky-line-delta]');
    var remove = event.target.closest('[data-ky-line-remove]');
    if (!delta && !remove) return;
    var quantity = remove ? 0 : Number(line.dataset.lineQuantity) + Number(delta.dataset.kyLineDelta);
    if (remove && line.hasAttribute('data-ky-hw-line')) {
      /* Removing the Halloween gift by hand also forgets the saved pick, so it is not added back. */
      try { localStorage.removeItem('ky-hw-choice'); } catch (error) { /* optional */ }
    }
    event.target.disabled = true;
    changeLine(line.dataset.lineKey, quantity).catch(function () {
      event.target.disabled = false;
    });
  }

  function init() {
    root = document.getElementById('KyCartDrawer');
    if (!root || root.dataset.kyCartReady === 'true') return;
    root.dataset.kyCartReady = 'true';
    panel = root.querySelector('.kycart');
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && root.classList.contains('is-open')) close();
    });
    document.addEventListener('ky:cart:open', function () {
      open();
      refresh();
    });
    document.addEventListener('ky:cart:refresh', function () {
      refresh();
    });
    cleanupLegacyGift();
  }

  window.KYCart = {
    open: open,
    close: close,
    add: add,
    changeLine: changeLine,
    refresh: refresh
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
